import { TileLayout } from "@progress/kendo-react-layout";
import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef, useMemo } from "react";
import ImageComponent from "../Components/Overview/ImageComponent";
import EndpointTableComponent from "../Components/Overview/Tables/EndpointTableComponent";
import ChartOverviewTileComponent, { ChartOverviewTileComponentRef } from "../Components/Overview/Charts/ChartOverviewTileComponent";
import GatewayTableComponent from "../Components/Overview/Tables/GatewayTableComponent";
import GroupsTableComponent from "../Components/Overview/Tables/GroupsTableComponent";
import GreasePumpsTableComponent from "../Components/Overview/Tables/GreasePumpsTableComponent";
import FuelBurnChartComponent, { FuelBurnChartComponentRef } from "../Components/Overview/Charts/FuelBurnChartComponent";
import { useQuery, useQueryClient } from "react-query";
import assetsDashboardApiService from "../Utilities/AssetsDashboardApiService";
import LoadingSpinner from "../../../Shared/Components/LoadingSpinner";
import ErrorCard from "../../../Shared/Components/Cards/ErrorCard";
import SelectedAssetMetricsReadDto from "../../../../Models/Dashboard/SelectedAssetMetricsReadDto";
import AssetImageReadDto from "../../../../Models/Assets/AssetImageReadDto";
import { AlertsPopover, AlertsPopoverRef } from "../Components/Overview/Popovers/AlertsPopover";
import MapComponent from "../Components/Overview/MapComponent";
import { DropDownList, DropDownListChangeEvent } from "@progress/kendo-react-dropdowns";
import { SvgIcon } from '@progress/kendo-react-common';
import { imageIcon } from '@progress/kendo-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMap } from '@fortawesome/free-solid-svg-icons';
import EntityNotesButton from "../Components/Notes/EntityNotesButton";

interface Props {
  asset: SelectedAssetMetricsReadDto;
  dateFilter: { startDate: Date, endDate: Date };
  setDateFilter: (filter: { startDate: Date, endDate: Date }) => void;
}

export interface AssetOverviewTabRef {
  refresh: () => Promise<void>;
}

// Computes rowHeight so the TileLayout exactly fills the measured container height.
// Replaces the previous viewport-based offset hack which left dead space at the bottom.
// `tileLayoutMargin` accounts for the 10px outer margin set on the TileLayout style (10 top + 10 bottom).
const calculateRowHeight = (containerHeight: number, totalRows: number): number => {
  const tileLayoutMargin = 20;
  const totalGapHeight = (totalRows - 1) * 10;
  const availableHeight = containerHeight - tileLayoutMargin - totalGapHeight;
  const rowHeight = Math.floor(availableHeight / totalRows);
  return Math.max(60, Math.min(rowHeight, 200));
};

const AssetOverviewTab = forwardRef<AssetOverviewTabRef, Props>(({ asset, dateFilter, setDateFilter }, ref) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ rowHeight: 110});
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [selectedEndpoint, setSelectedEndpoint] = useState<any | null>(null);
  const [selectedEndpointId, setSelectedEndpointId] = useState<string | null>(null);
  const [isGroupsSelected, setIsGroupsSelected] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [selectedTab, setSelectedTab] = useState(0);
  const [showMap, setShowMap] = useState(true);

  // Dropdown options for view selection
  const viewOptions = useMemo(() => [
    { text: "Map", value: true, icon: faMap },
    { text: "Image", value: false, icon: imageIcon }
  ], []);

  // Custom render for dropdown items
  const itemRender = (li: React.ReactElement<any>, itemProps: any) => {
    const item = itemProps.dataItem;
    const icon = item.icon;
    // Check if it's a FontAwesome icon (has iconName or prefix property)
    const isFontAwesome = icon && typeof icon === 'object' && (icon.iconName || icon.prefix);
    
    return React.cloneElement(li, li.props, 
      <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {isFontAwesome ? (
          <><FontAwesomeIcon icon={icon} /> <span>{item.text}</span></>
        ) : (
          <><SvgIcon icon={icon} /> <span>{item.text}</span></>
        )}
      </span>
    );
  };

  // Custom render for selected value
  const valueRender = (element: React.ReactElement<any>, value: any) => {
    if (!value) {
      return element;
    }
    const icon = value.icon;
    // Check if it's a FontAwesome icon (has iconName or prefix property)
    const isFontAwesome = icon && typeof icon === 'object' && (icon.iconName || icon.prefix);
    
    return (
      <span style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '8px' }}>
        {isFontAwesome ? (
          <FontAwesomeIcon icon={icon}/>
        ) : (
          <SvgIcon icon={icon} size="small" />
        )}
      </span>
    );
  };

  const handleViewChange = (event: DropDownListChangeEvent) => {
    const selectedOption = event.value;
    if (selectedOption && typeof selectedOption === 'object' && 'value' in selectedOption) {
      setShowMap(selectedOption.value);
    }
  };
  const chartRef = useRef<ChartOverviewTileComponentRef>(null);
  const fuelBurnChartRef = useRef<FuelBurnChartComponentRef>(null);
  const alertsRef = useRef<AlertsPopoverRef>(null);

  const queryClient = useQueryClient();
  // Track every endpoint whose charts were loaded while this tab was mounted so
  // their cached queries can be dropped on unmount (see cleanup effect below).
  const visitedEndpointKeysRef = useRef<Set<string>>(new Set());



  const imageQuery = useQuery<AssetImageReadDto>(
    ['assetImage', asset.assetConfigurationDetails.imageId],
    async () => {
      return assetsDashboardApiService.getAssetImage(asset.assetConfigurationDetails.imageId);
    },
    {
      // Keep the old data while fetching new data
      keepPreviousData: true
    }
  );

  const currentAsset = asset;
  const machineryType = (currentAsset as any)?.machineryType;

  // Query for GPS data (only for Locomotives)
  const locoGpsQuery = useQuery(
    ['locoGps', asset.id],
    async () => {
      return assetsDashboardApiService.getLocoGps(asset.id);
    },
    {
      enabled: machineryType === "Locomotive",
      keepPreviousData: true
    }
  );

  const doubleColumnPositions = useMemo(() => {
    const isGreasePumpEnabled = currentAsset?.assetConfigurationDetails?.isGreasePumpConnectivityEnabled ?? false;
    const isLocomotive = machineryType === "Locomotive";
    
    return !isGroupsSelected
      ? isLocomotive
        ? [
            { col: 1, colSpan: 3, rowSpan: 8, row: 1 },  // Image/Map (expanded to fill Gateway space)
            { col: 4, colSpan: 4, rowSpan: 3, row: 1 },  // Groups
            { col: 4, colSpan: 4, rowSpan: 5, row: 4 }   // Fuel Burn
          ]
        : isGreasePumpEnabled
          ? [
              { col: 1, colSpan: 3, rowSpan: 5, row: 1 },  // Image
              { col: 1, colSpan: 3, rowSpan: 3, row: 6 },  // Gateway
              { col: 4, colSpan: 4, rowSpan: 5, row: 1 },  // Groups
              { col: 4, colSpan: 4, rowSpan: 3, row: 6 }   // Grease Pumps
            ]
          : [
              { col: 1, colSpan: 3, rowSpan: 5, row: 1 },  // Image
              { col: 1, colSpan: 3, rowSpan: 3, row: 6 },  // Gateway
              { col: 4, colSpan: 4, rowSpan: 8, row: 1 }   // Groups (expanded)
            ]
      : isLocomotive
        ? [
            { col: 1, colSpan: 3, rowSpan: 8, row: 1 },   // Image/Map (expanded, no Gateway)
            { col: 4, colSpan: 4, rowSpan: 8, row: 1 }    // Endpoints
          ]
        : [
            { col: 1, colSpan: 3, rowSpan: 5, row: 1 },   // Image
            { col: 1, colSpan: 3, rowSpan: 3, row: 6 },   // Gateway
            { col: 4, colSpan: 4, rowSpan: 8, row: 1 }    // Endpoints
          ];
  }, [isGroupsSelected, currentAsset?.assetConfigurationDetails?.isGreasePumpConnectivityEnabled, machineryType]);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const updateDimensions = () => {
      const desktopRows = Math.max(
        ...doubleColumnPositions.map(pos => pos.row + pos.rowSpan - 1)
      );
      setDimensions({
        rowHeight: calculateRowHeight(node.clientHeight, desktopRows),
      });
    };

    updateDimensions();

    const observer = new ResizeObserver(updateDimensions);
    observer.observe(node);
    return () => observer.disconnect();
  }, [isGroupsSelected, doubleColumnPositions]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Handler for selecting a group
  const handleMarkerSelect = (groupId: number | null) => {
    setSelectedGroupId(groupId);
    setIsGroupsSelected(groupId !== null);
    if (groupId === null) {
      setSelectedEndpoint(null);
      setSelectedEndpointId(null);
    }
  };

  // Handler for deselecting all groups
  const handleDeselectAll = () => {
    setSelectedGroupId(null);
    setIsGroupsSelected(false);
    setSelectedEndpoint(null);
    setSelectedEndpointId(null);
  };

  // Handler for selecting an endpoint
  const handleEndpointSelect = (endpoint: any | null) => {
    setSelectedEndpoint(endpoint);
    setSelectedEndpointId(endpoint ? endpoint.id.toString() : null);
    if (endpoint?.id) visitedEndpointKeysRef.current.add(endpoint.id.toString());
  };

  // Drop cached chart queries when leaving the Overview tab so the charts
  // recompute from scratch on the next visit instead of showing stale cached
  // data. Mirrors the per-endpoint matching used by the refresh() handlers.
  useEffect(() => {
    const visited = visitedEndpointKeysRef.current;
    return () => {
      if (visited.size === 0) return;
      const keys = Array.from(visited);
      queryClient.removeQueries({
        predicate: (query) => {
          if (!Array.isArray(query.queryKey)) return false;
          const keyStr = JSON.stringify(query.queryKey);
          return keys.some((k) => keyStr.includes(k));
        },
      });
    };
  }, [queryClient]);

  const tiles = [
    {
      id: 'image',
      body: (
        <div className="h-full flex flex-col gap-1">
          <div className="flex items-center justify-between relative min-h-10 py-2">
            {machineryType === "Locomotive" ? (
              <div className="z-1 dropdown-menu-expanded-100">
                <DropDownList
                  data={viewOptions}
                  textField="text"
                  dataItemKey="value"
                  value={viewOptions.find(opt => opt.value === showMap)}
                  onChange={handleViewChange}
                  itemRender={itemRender}
                  valueRender={valueRender}
                  fillMode="flat"
                  size="small"
                  style={{ border: 'none' }}
                />
              </div>
            ) : (
              <div className="flex-1 min-w-0" aria-hidden />
            )}
            <div className="absolute left-1/2 transform -translate-x-1/2">
              <AlertsPopover ref={alertsRef} assetData={currentAsset} />
            </div>
            <div className="flex-1 min-w-0 flex justify-end">
              <EntityNotesButton
                entityType="Asset"
                entityId={currentAsset?.id ?? ''}
                entityName={currentAsset?.name}
              />
            </div>
          </div>
          {showMap && machineryType === "Locomotive" ? (
            locoGpsQuery.isLoading ? (
              <div className="w-full h-full flex items-center justify-center">
                <LoadingSpinner />
              </div>
            ) : locoGpsQuery.isError ? (
              <div className="w-full h-full flex items-center justify-center">
                <div className="text-red-500">Error loading GPS data</div>
              </div>
            ) : (
              <MapComponent 
                lat={locoGpsQuery.data?.lat ?? 0}
                lng={locoGpsQuery.data?.long ?? 0}
                markerName={currentAsset?.name || "Asset Location"}
                compliance={currentAsset?.complianceStatus || "Unknown"}
                connectivity={currentAsset?.connectivityStatus || "Unknown"}
                selectedGroupId={selectedGroupId}
                onMarkerSelect={handleMarkerSelect}
                firstGroupId={currentAsset?.assetConfigurationDetails?.groups?.[0] ? parseInt(currentAsset.assetConfigurationDetails.groups[0].id) : null}
                gpsData={locoGpsQuery.data || null}
                assetData={currentAsset}
              />
            )
          ) : (
            <ImageComponent 
              selectedGroupId={selectedGroupId}
              selectedGreasePumpId={selectedEndpoint?.greasePumpId || null}
              onMarkerSelect={handleMarkerSelect}
              assetData={currentAsset}
              imageData={imageQuery.data!}
            />
          )}
        </div>
      ),
      reorderable: false,
    },
    // Only show Gateway tile if not Locomotive
    ...(machineryType !== "Locomotive" ? [{
      id: 'gateway',
      body: (
        <div className="h-full flex flex-col p-2">
          <GatewayTableComponent assetData={currentAsset}/>
        </div>
      ),
      reorderable: false,
    }] : []),
    // Conditionally add tiles
    ...(!isGroupsSelected
      ? [
          // Show Groups tile (always shown when no group is selected)
          {
            id: 'groups',
            body: (
              <div className="h-full flex flex-col p-2">
                <GroupsTableComponent
                    assetData={currentAsset}
                    selectedGroupId={selectedGroupId}
                    onGroupSelect={handleMarkerSelect}
                />
              </div>
            ),
            reorderable: false,
          },
          // Show Fuel Burn tile only for Locomotive
          ...(machineryType === "Locomotive" ? [{
            id: 'fuelBurn',
            body: (
              <FuelBurnChartComponent 
                ref={fuelBurnChartRef}
                assetData={currentAsset}
                dateFilter={dateFilter}
                setDateFilter={setDateFilter}
              />
            ),
            reorderable: false,
          }] : []),
          // Only show grease pumps tile if grease pump connectivity is enabled and not Locomotive
          ...(machineryType !== "Locomotive" && currentAsset?.assetConfigurationDetails?.isGreasePumpConnectivityEnabled ? [{
            id: 'greasePumps',
            body: (
              <div className="h-full flex flex-col p-2">
                <GreasePumpsTableComponent assetData={currentAsset}/>
              </div>
            ),
            reorderable: false,
          }] : [])
        ]
      : [
          {
            id: 'endpoints',
            body: (
              <div className="h-full flex flex-col p-2 gap-5">
                <div style={{ maxHeight: 270, overflow: 'auto' }}>
                  <EndpointTableComponent
                    assetData={currentAsset}
                    selectedGroupId={selectedGroupId}
                    selectedEndpointId={selectedEndpointId}
                    onEndpointSelect={handleEndpointSelect}
                    onBack={handleDeselectAll}
                  />
                </div>
                <div style={{ flex: 1, minHeight: 0 }}>
                  {selectedEndpoint === null
                    ? <LoadingSpinner />
                    : <ChartOverviewTileComponent
                        ref={chartRef}
                        assetId={currentAsset?.id ?? ''}
                        assetName={currentAsset?.name}
                        selectedEndpoint={selectedEndpoint}
                        eventFilter={dateFilter}
                        setEventFilter={setDateFilter}
                        selectedTab={selectedTab}
                        onTabChange={setSelectedTab}
                        isGreasePumpConnectivityEnabled={currentAsset?.assetConfigurationDetails?.isGreasePumpConnectivityEnabled}
                        machineryType={machineryType}
                      />
                  }
                </div>
              </div>
            ),
            reorderable: false,
          }
        ]
    )
  ];

  const singleColumnPositions = !isGroupsSelected
    ? machineryType === "Locomotive"
      ? [
          // Image/Map (reduced size)
          { col: 1, colSpan: 1, rowSpan: 5, row: 1 },
          // Groups (increased size)
          { col: 1, colSpan: 1, rowSpan: 3, row: 6 },
          // Fuel Burn
          { col: 1, colSpan: 1, rowSpan: 5, row: 9 }
        ]
      : currentAsset?.assetConfigurationDetails?.isGreasePumpConnectivityEnabled
        ? [
            // Image
            { col: 1, colSpan: 1, rowSpan: 4, row: 1 },
            // Groups
            { col: 1, colSpan: 1, rowSpan: 2, row: 5 },
            // Gateway
            { col: 1, colSpan: 1, rowSpan: 4, row: 7 },
            // Grease Pumps
            { col: 1, colSpan: 1, rowSpan: 2, row: 11 }
          ]
        : [
            // Image
            { col: 1, colSpan: 1, rowSpan: 4, row: 1 },
            // Groups (expanded)
            { col: 1, colSpan: 1, rowSpan: 4, row: 5 },
            // Gateway
            { col: 1, colSpan: 1, rowSpan: 4, row: 9 }
          ]
    : machineryType === "Locomotive"
      ? [
          // Image/Map (reduced size)
          { col: 1, colSpan: 1, rowSpan: 5, row: 1 },
          // Endpoints
          { col: 1, colSpan: 1, rowSpan: 7, row: 6 },
        ]
      : [
          // Image
          { col: 1, colSpan: 1, rowSpan: 4, row: 1 },
          // Gateway
          { col: 1, colSpan: 1, rowSpan: 2, row: 5 },
          // Endpoints
          { col: 1, colSpan: 1, rowSpan: 7, row: 7 },
        ];


  useImperativeHandle(ref, () => ({
    refresh: async () => {
      await Promise.all([
        imageQuery.refetch(),
        alertsRef.current?.refresh(),
        selectedEndpoint && chartRef.current?.refresh(),
        machineryType === "Locomotive" && fuelBurnChartRef.current?.refresh()
      ]);
    }
  }));

  if (imageQuery.isLoading) {
    return <LoadingSpinner />;
  } else if (imageQuery.isError) {
    return <ErrorCard/>;
  }

  return (
    <div ref={containerRef} className="flex flex-1 min-h-0 flex-col">
      {isMobile ? (
        <TileLayout
          columns={1}
          rowHeight={108}
          columnWidth="100%"
          positions={singleColumnPositions}
          gap={{ rows: 10, columns: 10 }}
          items={tiles}
          style={{ padding: 0, margin: 10, borderRadius: 8 }}
          className="dashboard-page-tile"
        />
      ) : (
        <TileLayout
          columns={7}
          rowHeight={dimensions.rowHeight}
          columnWidth="14.28%" // 100 / 7
          positions={doubleColumnPositions}
          gap={{ rows: 10, columns: 10 }}
          items={tiles}
          style={{ padding: 0, margin: 10, borderRadius: 8 }}
          className="dashboard-page-tile dashboard-custom-tile-layout"
        />
      )}
    </div>
  );
});

export default AssetOverviewTab;